import { useContext } from 'react';
import { AppContext } from '../../../../common';
import { BaseConfirmModal } from '../../../../common/components/modals';
import { ACTIONS } from '../../../../constants';
import { getActionService } from '../../../../utils/getActionService';
import { CostCenterModel } from '../../../../models';
import { OperationCenterType } from '../../../../models/operations-center/operationsCenterType';
import './styles/DetailsModalStyle.css';

export interface DataInfo {
    start: string;
    end: string;
    title: string;
    className: string;
    extendedProps: DataInfoExtendedProps;
}

export interface DataInfoExtendedProps {
    employee: string | undefined;
    documentNumber: string | undefined;
    isNextDay: boolean | undefined | null;
    isDuplex: boolean | undefined | null;
    isExceptional: boolean | undefined | null;
    workshiftName: string | undefined;
    interruptionName: string | undefined;
    startTime: string | undefined | null;
    endTime: string | undefined | null;
    startTime2: string | undefined | null;
    endTime2: string | undefined | null;
    observations: string | undefined;
    date: string | undefined;
    personId: number;
    workshiftId: number | null;
    interruptionId: number | null;
    hasAvailability: boolean | null;
    isAdditionalHour: boolean | null;
    interruptionTypeId: number | null;
    costCenters: CostCenterModel[] | null;
    operationCenters: OperationCenterType[] | null;
}

interface DetailsModalProps {
    isOpen: boolean;
    detailsInfo: DataInfo;
    handlerCreateRequestFromButton: (
        type: string,
        startDate: string,
        endDate: string,
        startHour: string,
        endHour: string,
        info: DataInfoExtendedProps
    ) => void;
    onClose: (newState: boolean) => void;
}

export const DetailsModal = ({
    isOpen,
    onClose,
    detailsInfo,
    handlerCreateRequestFromButton,
}: DetailsModalProps) => {
    const { isSkinSmart, userInfo } = useContext(AppContext);

    const handlerClose = () => {
        onClose(false);
    };
    const hanlderSubmit = () => {
        onClose(false);
    };

    const handlerCreateRequest = () => {
        handlerCreateRequestFromButton(
            detailsInfo.className,
            detailsInfo.start,
            detailsInfo.end,
            detailsInfo.extendedProps.startTime ?? '',
            detailsInfo.extendedProps.endTime ?? '',
            detailsInfo.extendedProps
        );
    };

    const convertHourAMPM = (hour: string) => {
        let formatedHour = hour.split(':')[0] + ':' + hour.split(':')[1];
        // Parsea la cadena de texto a un objeto Date
        const hourDate = new Date(`2000-01-01T${formatedHour}`);

        // Obtiene las horas y minutos
        let hours = hourDate.getHours();
        const minutes = hourDate.getMinutes() ? hourDate.getMinutes() : 0;

        // Determina si es am o pm
        const ampm = hours >= 12 ? 'pm' : 'am';

        // Convierte a formato de 12 hours
        hours = hours % 12 || 12; // Si son las 12, muestra 12 en lugar de 0

        // Asegura que los minutes siempre tengan dos dígitos
        const minutesFormateados = minutes < 10 ? `0${minutes}` : minutes;

        // Retorna la hora en formato am/pm
        return `${hours}:${minutesFormateados} ${ampm}`;
    };

    const showManageRequests = () => {
        return (
            getActionService(userInfo.actions, ACTIONS['approved.scheduling']) ||
            getActionService(userInfo.actions, ACTIONS['approve.interruptions']) ||
            getActionService(userInfo.actions, ACTIONS['approve.covers'])
        );
    };

    const costCenters = detailsInfo.extendedProps?.costCenters ?? [];
    const costCentersCount = costCenters.length;

    return (
        <BaseConfirmModal
            isOpen={isOpen}
            modalTitle={'Detalles'}
            onClose={handlerClose}
            onSubmit={hanlderSubmit}
            disableSubmit={false}
            isShowControls={false}
            isLoading={false}
        >
            <div className="modal-body text-black">
                <div className="d-flex justify-content-between align-items-start">
                    <div>
                        <h4 className="mb-1 d-flex align-items-center gap-2">
                            <span>
                                {detailsInfo.extendedProps?.employee ?? 'Empleado'}
                            </span>
                            <span className="badge rounded-pill text-bg-light">
                                {costCentersCount} CC
                            </span>
                        </h4>

                        <div className="d-flex flex-wrap gap-2">
                            {detailsInfo.extendedProps?.documentNumber && (
                                <span className="badge bg-secondary-subtle text-secondary-emphasis">
                                    Identificación: {detailsInfo.extendedProps.documentNumber}
                                </span>
                            )}

                            {detailsInfo?.extendedProps?.hasAvailability === true && (
                                <span className="badge bg-success-subtle text-success-emphasis">
                                    Con disponibilidad asignada
                                </span>
                            )}
                        </div>
                    </div>

                    {showManageRequests() && detailsInfo.className !== 'additional' && (
                        <button
                            onClick={handlerCreateRequest}
                            className="btn btn-sm btn-primary"
                        >
                            Crear solicitud
                        </button>
                    )}
                </div>

                <div className="mt-4">
                    <h6 className="text-uppercase text-muted mb-2">Información</h6>

                    {detailsInfo.className?.includes('workshift') ? (
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <div className="small text-muted mb-1">
                                            Turno
                                        </div>
                                        <div className="fw-semibold">
                                            {detailsInfo.extendedProps?.workshiftName ?? '-'}
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="small text-muted mb-1">
                                            Fecha
                                        </div>
                                        <div className="fw-semibold">
                                            {detailsInfo.start ?? '-'}
                                        </div>
                                    </div>
                                </div>
                                <div className="row g-2 mt-3">
                                    {detailsInfo.extendedProps?.startTime && (
                                        <div className="col-6 col-md-3">
                                            <div className="border rounded-3 p-2 h-100">
                                                <div className="small text-muted">
                                                    Entrada
                                                </div>
                                                <div className="fw-bold">
                                                    {convertHourAMPM(detailsInfo.extendedProps.startTime)}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {detailsInfo.extendedProps?.endTime && (
                                        <div className="col-6 col-md-3">
                                            <div className="border rounded-3 p-2 h-100">
                                                <div className="small text-muted">
                                                    Salida
                                                </div>
                                                <div className="fw-bold">
                                                    {convertHourAMPM(detailsInfo.extendedProps.endTime)}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {detailsInfo.extendedProps?.isDuplex &&
                                        detailsInfo.extendedProps.startTime2 && (
                                            <div className="col-6 col-md-3">
                                                <div className="border rounded-3 p-2 h-100">
                                                    <div className="small text-muted">
                                                        2da Entrada
                                                    </div>
                                                    <div className="fw-bold">
                                                        {convertHourAMPM(detailsInfo.extendedProps.startTime2)}
                                                    </div>
                                                </div>
                                            </div>
                                    )}

                                    {detailsInfo.extendedProps?.isDuplex &&
                                        detailsInfo.extendedProps.endTime2 && (
                                        <div className="col-6 col-md-3">
                                            <div className="border rounded-3 p-2 h-100">
                                                <div className="small text-muted">
                                                    2da Salida
                                                </div>
                                                <div className="fw-bold">
                                                    {convertHourAMPM(detailsInfo.extendedProps.endTime2)}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    <span
                                        className={
                                            'badge ' +
                                            (detailsInfo.extendedProps?.isNextDay
                                            ? 'bg-info-subtle text-info-emphasis'
                                            : 'bg-light text-muted border')
                                        }
                                        title="Cambia de día"
                                    >
                                        Cambia de día: {detailsInfo.extendedProps?.isNextDay ? 'Sí' : 'No'}
                                    </span>

                                    <span
                                        className={
                                            'badge ' +
                                            (detailsInfo.extendedProps?.isDuplex
                                            ? 'bg-primary-subtle text-primary-emphasis'
                                            : 'bg-light text-muted border')
                                        }
                                        title="Turno doble"
                                    >
                                        Doble: {detailsInfo.extendedProps?.isDuplex ? 'Sí' : 'No'}
                                    </span>

                                    <span
                                        className={
                                            'badge ' +
                                            (detailsInfo.extendedProps?.isExceptional
                                            ? 'bg-warning-subtle text-warning-emphasis'
                                            : 'bg-light text-muted border')
                                        }
                                        title="Excepcional"
                                    >
                                        Excepcional:{' '}
                                        {detailsInfo.extendedProps?.isExceptional ? 'Sí' : 'No'}
                                    </span>
                                </div> 

                                <div className="mt-4">
                                    <div className="row g-3">
                                        <div className="col-12 col-lg-6">
                                            <h6 className="text-uppercase text-muted mb-2 d-flex align-items-center gap-2">
                                                <span>Centros de costo</span>

                                                {(detailsInfo.extendedProps?.costCenters?.length ?? 0) > 0 && (
                                                    <span className="badge rounded-pill text-bg-light">
                                                        {detailsInfo.extendedProps?.costCenters?.length}
                                                    </span>
                                                )}
                                            </h6>

                                            {(detailsInfo.extendedProps?.costCenters?.length ?? 0) === 0 ? (
                                                <div className="alert alert-light border d-flex align-items-center py-2 m-0">
                                                    <span className="small">Sin centros de costo asociados.</span>
                                                </div>
                                            ) : (
                                                <div className="card border-0 shadow-sm">
                                                    <div className="card-body p-2 card-body-list">
                                                        <ul className="list-group list-group-flush">
                                                            {detailsInfo.extendedProps?.costCenters?.map(
                                                                (cc: any, i: number) => {
                                                                    const name = cc?.costCenter?.name ?? cc?.name ?? 'Sin nombre';
                                                                    const observations = cc?.costCenter?.observations ?? cc?.observations ?? '';

                                                                    return (
                                                                        <li key={cc?.id ?? i} className="list-group-item d-flex justify-content-between align-items-start px-2 py-2">
                                                                            <div className="me-auto">
                                                                                <div className="fw-semibold">
                                                                                    {name}
                                                                                </div>
                                                                                {observations && (
                                                                                    <div className="small text-muted text-truncate">
                                                                                        {observations}
                                                                                    </div>
                                                                                )}
                                                                            </div> 
                                                                        </li>
                                                                    );
                                                                }
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-12 col-lg-6">
                                            <h6 className="text-uppercase text-muted mb-2 d-flex align-items-center gap-2">
                                                <span>Centros de operación</span> 
                                            </h6>

                                            {(detailsInfo.extendedProps?.operationCenters?.length ?? 0) === 0 ? (
                                                <div className="alert alert-light border d-flex align-items-center py-2 m-0">
                                                    <span className="small">
                                                        Sin centros de operación asociados.
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="card border-0 shadow-sm">
                                                    <div className="card-body p-2 card-body-list">
                                                        <ul className="list-group list-group-flush">
                                                            {detailsInfo.extendedProps?.operationCenters?.map(
                                                                (oc: any, i: number) => {
                                                                    const name = oc?.operationCenters?.name ?? oc?.name ?? 'Sin nombre';
                                                                    const observations = oc?.operationCenters?.observations ?? oc?.observations ?? '';

                                                                    return (
                                                                        <li key={oc?.id ?? i} className="list-group-item d-flex justify-content-between align-items-start px-2 py-2">
                                                                            <div className="me-auto">
                                                                                <div className="fw-semibold">
                                                                                    {name}
                                                                                </div>
                                                                                {observations && (
                                                                                    <div className="small text-muted text-truncate">
                                                                                        {observations} 
                                                                                    </div>
                                                                                )}
                                                                            </div> 
                                                                        </li>
                                                                    );
                                                                }
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <div className="small text-muted mb-1">
                                            Programación
                                        </div>
                                        <div className="fw-semibold">
                                            {detailsInfo.title ?? '-'}
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-6">
                                        <div className="small text-muted mb-1">
                                            Fecha
                                        </div>
                                        <div className="fw-semibold">
                                            {detailsInfo.start?.split('T')[0] ?? '-'}
                                        </div>
                                    </div>

                                    <div className="col-6 col-md-3">
                                        <div className="border rounded-3 p-2 h-100">
                                            <div className="small text-muted">
                                                Inicio
                                            </div>
                                            <div className="fw-bold">
                                                {convertHourAMPM(
                                                    detailsInfo.extendedProps.startTime ?? ''
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-6 col-md-3">
                                        <div className="border rounded-3 p-2 h-100">
                                            <div className="small text-muted">
                                                Final
                                            </div>
                                            <div className="fw-bold">
                                                {convertHourAMPM(
                                                    detailsInfo.extendedProps.endTime ?? ''
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </BaseConfirmModal>
    );
};